import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Session {
  id: string
  user_email: string
  started_at: string
  ended_at: string | null
  duration_minutes: number
  pages_viewed: number
  actions_taken: number
  device: string
  browser: string
  country: string
}

interface PageView {
  page: string
  views: number
  avg_time: number
}

export default function UserActivityPage() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [pageViews, setPageViews] = useState<PageView[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedSession, setSelectedSession] = useState<Session | null>(null)
  const [journey, setJourney] = useState<any[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [sessRes, pagesRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/user-activity/sessions'),
      fetch(API_BASE + '/user-activity/pages'),
      fetch(API_BASE + '/user-activity/statistics')
    ])
    if (sessRes.ok) setSessions((await sessRes.json()).sessions || [])
    if (pagesRes.ok) setPageViews((await pagesRes.json()).pages || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const viewJourney = async (session: Session) => {
    setSelectedSession(session)
    const res = await fetch(API_BASE + '/user-activity/sessions/' + session.id + '/journey')
    if (res.ok) setJourney((await res.json()).journey || [])
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">User Activity & Session Analytics</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Sessions</div>
              <div className="text-2xl font-bold">{statistics.total_sessions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Now</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_sessions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Page Views</div>
              <div className="text-2xl font-bold">{statistics.total_page_views?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Duration</div>
              <div className="text-2xl font-bold">{statistics.avg_session_duration} min</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Top Page</div>
              <div className="text-lg font-bold text-blue-600">{statistics.top_page}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Active Sessions</h2></div>
              <div className="divide-y">
                {sessions.map(sess => (
                  <div key={sess.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => viewJourney(sess)}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{sess.user_email}</div>
                        <div className="text-sm text-gray-500">{sess.device} / {sess.browser} / {sess.country}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm ${sess.ended_at ? 'text-gray-500' : 'text-green-600 font-medium'}`}>
                          {sess.ended_at ? 'Ended' : 'Active'}
                        </div>
                        <div className="text-sm text-gray-400">{sess.duration_minutes} min</div>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                      <span>{sess.pages_viewed} pages</span>
                      <span>{sess.actions_taken} actions</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Page Analytics</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pageViews.map(page => (
                    <tr key={page.page}>
                      <td className="px-6 py-4 font-medium">{page.page}</td>
                      <td className="px-6 py-4">{page.views.toLocaleString()}</td>
                      <td className="px-6 py-4">{Math.floor(page.avg_time / 60)}m {page.avg_time % 60}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            {selectedSession && journey.length > 0 ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">User Journey</h3>
                <div className="text-sm text-gray-500 mb-4">{selectedSession.user_email}</div>
                <div className="space-y-4">
                  {journey.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">{idx + 1}</div>
                      <div>
                        <div className="font-medium">{step.page}</div>
                        <div className="text-xs text-gray-500">{step.duration}s</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Session Details</h3>
                <p className="text-gray-500 text-sm">Select a session to view user journey</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
